import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { apiService } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const CompanyInfoManagement = () => {
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCompanyInfo();
  }, []);

  const loadCompanyInfo = async () => {
    try {
      const response = await apiService.getCompanyInfo();
      setCompanyInfo(response.data);
    } catch (error) {
      toast({
        title: 'Hata',
        description: 'Şirket bilgileri yüklenemedi.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const data = {
      name: formData.get('name'),
      slogan_tr: formData.get('slogan_tr'),
      slogan_en: formData.get('slogan_en'),
      description_tr: formData.get('description_tr'),
      description_en: formData.get('description_en'),
      about_tr: formData.get('about_tr'),
      about_en: formData.get('about_en'),
      logo_url: formData.get('logo_url'),
      stats: {
        monthly_shipments: formData.get('monthly_shipments'),
        employees: formData.get('employees'),
        vehicles: formData.get('vehicles'),
      },
    };

    try {
      await apiService.updateCompanyInfo(data);
      toast({ title: 'Başarılı', description: 'Şirket bilgileri güncellendi.' });
      loadCompanyInfo();
    } catch (error) {
      toast({
        title: 'Hata',
        description: 'Güncelleme başarısız.',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-[#36344D] mb-6">Şirket Bilgileri</h2>

      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md">
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4 text-[#DF6A6A]">Genel Bilgiler</h3>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block font-semibold mb-2">Şirket Adı</label>
              <input
                name="name"
                defaultValue={companyInfo?.name}
                required
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#DF6A6A]"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">Logo URL</label>
              <input
                name="logo_url"
                defaultValue={companyInfo?.logo_url}
                required
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#DF6A6A]"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block font-semibold mb-2">Slogan (TR)</label>
              <input
                name="slogan_tr"
                defaultValue={companyInfo?.slogan_tr}
                required
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#DF6A6A]"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">Slogan (EN)</label>
              <input
                name="slogan_en"
                defaultValue={companyInfo?.slogan_en}
                required
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#DF6A6A]"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block font-semibold mb-2">Kısa Açıklama (TR)</label>
              <textarea
                name="description_tr"
                defaultValue={companyInfo?.description_tr}
                required
                rows="3"
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#DF6A6A]"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">Short Description (EN)</label>
              <textarea
                name="description_en"
                defaultValue={companyInfo?.description_en}
                required
                rows="3"
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#DF6A6A]"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block font-semibold mb-2">Hakkımızda (TR)</label>
              <textarea
                name="about_tr"
                defaultValue={companyInfo?.about_tr}
                required
                rows="5"
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#DF6A6A]"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">About Us (EN)</label>
              <textarea
                name="about_en"
                defaultValue={companyInfo?.about_en}
                required
                rows="5"
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#DF6A6A]"
              />
            </div>
          </div>
        </div>

        <div className="mb-6 border-t pt-6">
          <h3 className="text-lg font-semibold mb-4 text-[#DF6A6A]">İstatistikler</h3>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block font-semibold mb-2">Aylık Taşıma</label>
              <input
                name="monthly_shipments"
                defaultValue={companyInfo?.stats?.monthly_shipments}
                required
                placeholder="10,000+"
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#DF6A6A]"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">Çalışan Sayısı</label>
              <input
                name="employees"
                defaultValue={companyInfo?.stats?.employees}
                required
                placeholder="5,000+"
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#DF6A6A]"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">Araç/Ekipman</label>
              <input
                name="vehicles"
                defaultValue={companyInfo?.stats?.vehicles}
                required
                placeholder="3,000+"
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-[#DF6A6A]"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="bg-[#DF6A6A] text-white px-8 py-3 rounded-md hover:bg-[#c55555] flex items-center space-x-2 font-semibold"
          >
            <Save className="w-5 h-5" />
            <span>Kaydet</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyInfoManagement;
